import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import { items } from "data-files/items";
import { quests } from "data-files/quests";
import { EorzeaDataType, GameData, InputType, Job, LinkType, RelicData, View } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewWeaponAnimaHyperconductiveProps } from "views/relics/weapons/anima/hyderconductive/types";
import "./WeaponAnimaHyperconductive.css";

const mapStateToProps = (state:AppState) => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
	};
};

class WeaponAnimaHyperconductive extends Component<ViewWeaponAnimaHyperconductiveProps> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA],
		type: RelicData.Type.WEAPON,
		relic: RelicData.Group.WEAPON_ANIMA,
		part: RelicData.Part.WEAPON_HYPERCONDUCTIVE,
		steps: [RelicData.Step.OIL_HYPER],
		stepPlaceholders: { [RelicData.Step.OIL_HYPER]: { ITEM: GameData.Item.AETHER_OIL } },
		next: {
			part: RelicData.Part.WEAPON_RECONDITIONED,
			view: View.RELIC_WEAPONS_ANIMA_RECONDITIONED,
		},
		previous: {
			part: RelicData.Part.WEAPON_ANIMA,
			view: View.RELIC_WEAPONS_ANIMA_ANIMA,
		},
	};

	constructor(props:ViewWeaponAnimaHyperconductiveProps){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);
		const taskItem = manager.relics.getTaskItem(RelicData.Step.OIL_HYPER, RelicData.Task.TASK_1, Job.PLD);
		const stepInventory = manager.data.getInventoryStatus({
			item: taskItem,
			relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.OIL_HYPER, RelicData.Task.TASK_1],
		});

		return (
			<div id="weapon-anima-hyperconductive" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
					<p>{t.t("The %PART% upgrade follows on from %PREVIOUSPART%", { PART: this.relicInfo.part, PREVIOUSPART: this.relicInfo.previous ? this.relicInfo.previous.part : "Unknown"})}</p>
					<p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={EorzeaDataType.BLUEQUEST} text={GameData.Quest.FINDING_YOUR_VOICE} lodestoneID={quests[GameData.Quest.FINDING_YOUR_VOICE].lodestoneID} /></p>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection title={RelicData.Step.OIL_HYPER} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.OIL_HYPER]} id={RelicData.Step.OIL_HYPER} stepComplete={completeSteps[RelicData.Step.OIL_HYPER]}>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Obtained")}</th><th>{t.t("Item")}</th><th>{t.t("Quantity / Job")}</th><th>{t.t("Total")}</th><th>{t.t("Used")}</th><th>{t.t("Remaining")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th></tr></thead>
								<tbody>
									{this.props.hideCompletedTasks && stepInventory.toObtain === 0
										? null
										: <tr className="odd-row">
											<td><Obtained obtained={items[taskItem].obtained} qtyNeeded={stepInventory.total} /></td>
											<td>{t.t(items[taskItem].name)}</td>
											<td>{stepInventory.qtyPerJob.toLocaleString()}</td>
											<td>{stepInventory.total.toLocaleString()}</td>
											<td>{stepInventory.used.toLocaleString()}</td>
											<td>{stepInventory.remaining.toLocaleString()}</td>
											<td><FormComponents.TextInput label={{ hide: true }} input={{ type: InputType.NUMBER, value: stepInventory.inventory, dataAttributes: { item: taskItem }, events: { onChange: manager.content.inventoryUpdated }}} /></td>
											<td>{stepInventory.toObtain.toLocaleString()}</td>
										</tr>
									}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.OIL_HYPER)
											? <tr className="all-complete"><td colSpan={8}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount(){
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		if(!this.relicInfo || this.relicInfo.previous === null){ return []; }
		return [
			{ type: "previous part", part: this.relicInfo.previous.part, partView: this.relicInfo.previous.view },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [GameData.NPC.ARDASHIR];
	}
}
export default connect(mapStateToProps)(WeaponAnimaHyperconductive);
